import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, EmptyState, SearchBar, normalize } from "@/components/Brutal";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer } from "lucide-react";

const empty = { nome: "", cpf: "", matricula: "", sector_id: "", funcao: "", telefone: "" };

export default function Collaborators() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [items, setItems] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [setorFilter, setSetorFilter] = useState("");

  const load = async () => {
    setLoading(true);
    const [a, b] = await Promise.all([api.get("/collaborators"), api.get("/sectors")]);
    setItems(a.data); setSectors(b.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, sector_id: form.sector_id || null };
      if (editing) await api.put(`/collaborators/${editing.id}`, payload);
      else await api.post("/collaborators", payload);
      toast.success(editing ? "Atualizado" : "Criado");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!confirm("Excluir?")) return; try { await api.delete(`/collaborators/${id}`); toast.success("Excluído"); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };

  const filtered = items.filter((c) => {
    if (setorFilter && (c.sector_id || "") !== setorFilter) return false;
    if (!q) return true;
    const nq = normalize(q);
    return normalize(c.nome).includes(nq) || normalize(c.cpf).includes(nq) || normalize(c.matricula).includes(nq) || normalize(c.funcao).includes(nq) || normalize(c.sector_nome).includes(nq) || normalize(c.telefone).includes(nq);
  });

  return (
    <div data-testid="collabs-page">
      <SectionTitle action={canEdit && <Btn onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}><Plus className="w-4 h-4" />Novo Colaborador</Btn>}>
        Colaboradores
      </SectionTitle>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <SearchBar value={q} onChange={setQ} placeholder="Buscar por nome, matrícula, CPF, função ou setor..." testid="collabs-search" />
        <select value={setorFilter} onChange={(e) => setSetorFilter(e.target.value)} className="border-2 border-[#111] p-2 text-sm bg-white font-mono-data" data-testid="collabs-setor-filter">
          <option value="">Todos os setores</option>
          {sectors.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <span className="text-xs font-mono-data text-[#555] ml-auto uppercase tracking-widest">
          <b className="text-base text-[#111]">{filtered.length}</b> / {items.length}
        </span>
      </div>
      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead><tr><th>Nome</th><th>Matrícula</th><th>Setor</th><th>Função</th><th>CPF</th><th>Telefone</th>{canEdit && <th className="w-24">Ações</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filtered.length === 0 && <EmptyState message={q || setorFilter ? "Nenhum resultado." : "Nenhum colaborador."} cols={canEdit ? 7 : 6} />}
            {filtered.map((c) => (
              <tr key={c.id}>
                <td className="font-medium">{c.nome}</td>
                <td className="font-mono-data text-xs">{c.matricula || "—"}</td>
                <td className="text-sm">{c.sector_nome || "—"}</td>
                <td className="text-sm">{c.funcao || "—"}</td>
                <td className="font-mono-data text-xs">{c.cpf || "—"}</td>
                <td className="font-mono-data text-xs">{c.telefone || "—"}</td>
                {canEdit && <td><div className="flex gap-1">
                  <a href={`/imprimir/colaborador/${c.id}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Ficha individual"><Printer className="w-3.5 h-3.5" /></a>
                  <button onClick={() => { setEditing(c); setForm({ ...c, sector_id: c.sector_id || "" }); setOpen(true); }} className="p-1.5 hover:bg-[#FF4F00] hover:text-white border border-[#111]"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar" : "Novo Colaborador"} size="md">
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <Field label="Nome *" required value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} colspan />
          <Field label="Matrícula" value={form.matricula} onChange={(v) => setForm({ ...form, matricula: v })} />
          <Field label="CPF" value={form.cpf} onChange={(v) => setForm({ ...form, cpf: v })} />
          <SelectField label="Setor" value={form.sector_id} onChange={(v) => setForm({ ...form, sector_id: v })}
            options={sectors.map((s) => ({ value: s.id, label: s.nome }))} />
          <Field label="Função" value={form.funcao} onChange={(v) => setForm({ ...form, funcao: v })} />
          <Field label="Telefone" value={form.telefone} onChange={(v) => setForm({ ...form, telefone: v })} colspan />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit">{editing ? "Salvar" : "Criar"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
